import React from 'react';
import type { BadgeTone } from './Badge';

/**
 * Button hierarchy.
 *
 * Emphasis is carried by weight and fill, not by hue: solid, then outlined,
 * then bare. That keeps the One Accent Rule intact — every ordinary control is
 * still the same accent — while making the ranking obvious at a glance.
 *
 * The one hue exception is `destructive`, because deleting is not a matter of
 * emphasis. Red there is semantic, not decorative.
 *
 * The rule that matters: **one solid button per view.** If two things are
 * filled, neither reads as the primary action. Everything supporting it is
 * `secondary`; everything incidental is `ghost`.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md';

const VARIANT: Record<ButtonVariant, string> = {
  // The single strongest action on a view. Deepens to navy on hover so the
  // press reads as going further in, not lighter.
  primary:
    'border border-accent bg-accent text-accent-foreground hover:bg-primary hover:border-primary',
  // Supporting actions. Outlined: clearly a control, clearly not the main one.
  secondary:
    'border border-accent/35 bg-card text-accent hover:bg-accent hover:border-accent hover:text-accent-foreground',
  // Incidental actions inside dense rows, where a border would add clutter.
  ghost:
    'border border-transparent bg-transparent text-muted-foreground hover:bg-secondary hover:text-accent',
  // Semantic, not emphatic. Stays quiet until hovered so it never competes.
  destructive:
    'border border-transparent bg-transparent text-muted-foreground hover:bg-status-red-soft hover:text-status-red-ink',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-7 px-3 text-[12.5px] gap-1.5',
  md: 'h-8 px-4 text-sm gap-2',
};

/**
 * Square variants, for a button whose whole label is its glyph.
 *
 * Three call sites were hand-rolling `className="w-7 px-0"` and each picked its
 * own glyph size, so the icon-only buttons came out inconsistent — and the one
 * on the opportunity masthead was a 13px glyph adrift in a 28px box.
 */
const ICON_SIZE: Record<ButtonSize, string> = {
  sm: 'h-7 w-7 p-0',
  md: 'h-8 w-8 p-0',
};

/** Glyph size to pass the lucide icon inside an `icon` button, per size. */
export const ICON_GLYPH: Record<ButtonSize, number> = { sm: 15, md: 17 };

/**
 * A bare glyph is not a visible target.
 *
 * `ghost` and `destructive` are transparent and muted at rest by design, which
 * works for a labelled control in a dense row — the words carry it. Strip the
 * words and nothing is left to see, which is how the delete control on the
 * opportunity masthead became invisible. So an icon-only button in either of
 * those variants keeps a hairline at rest.
 *
 * This is the Section Link's reasoning applied to a glyph: bare accent text at
 * label size does not read as pressable, and neither does a lone 15px icon.
 * The border is neutral, so no status hue leaks onto an interactive element.
 */
const ICON_RESTING_EDGE: Partial<Record<ButtonVariant, string>> = {
  ghost: 'border-border',
  destructive: 'border-border hover:border-status-red-soft',
};

const BASE =
  'inline-flex items-center justify-center font-medium rounded-control whitespace-nowrap ' +
  'transition-colors disabled:opacity-70 disabled:pointer-events-none ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The same classes, for things that are links rather than buttons.
 *
 * A control that navigates must be an `<a>`, so it can be opened in a new tab
 * and read as a link by a screen reader. Rather than teach Button to render as
 * something else, callers compose: `<Link className={buttonClass('secondary')}>`.
 */
export function buttonClass(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'sm',
  className = '',
  icon = false
): string {
  const shape = icon ? `${ICON_SIZE[size]} ${ICON_RESTING_EDGE[variant] ?? ''}` : SIZE[size];
  return `${BASE} ${shape} ${VARIANT[variant]} ${className}`;
}

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Icon-only: square, no padding, and a resting hairline where the variant
   * would otherwise be invisible. Pair with `ICON_GLYPH[size]` for the glyph,
   * and give it an `aria-label` — there is no text to read.
   */
  icon?: boolean;
};

export default function Button({
  variant = 'secondary',
  size = 'sm',
  className = '',
  type = 'button',
  icon = false,
  ...rest
}: Props) {
  return (
    <button
      type={type}
      className={buttonClass(variant, size, className, icon)}
      {...rest}
    />
  );
}

/**
 * Engaged tones for a filter that selects a *status*.
 *
 * This is the one sanctioned relaxation of the One Accent Rule. A filter chip
 * is not asserting interaction when it is on — it is naming the records it has
 * selected, and those records wear these exact tones in the list below. Making
 * "Overdue" engage red means clicking the red chip produces red badges, which
 * is a recognition win no amount of accent-blue can buy.
 *
 * It holds only because the *resting* state stays neutral. An unengaged chip is
 * always the same grey outline, so tone never competes to be clicked — it only
 * ever describes what is already on.
 */
const SEGMENT_TONE: Record<BadgeTone, string> = {
  blue: 'border-status-blue-ink/50 bg-status-blue-soft text-status-blue-ink',
  amber: 'border-status-amber-ink/50 bg-status-amber-soft text-status-amber-ink',
  green: 'border-status-green-ink/50 bg-status-green-soft text-status-green-ink',
  red: 'border-status-red-ink/50 bg-status-red-soft text-status-red-ink',
  slate: 'border-status-slate-ink/50 bg-status-slate-soft text-status-slate-ink',
  teal: 'border-status-teal-ink/50 bg-status-teal-soft text-status-teal-ink',
  violet: 'border-status-violet-ink/50 bg-status-violet-soft text-status-violet-ink',
};

/**
 * Segmented filter control. Deliberately NOT a button variant: an engaged
 * filter is a *state*, and filling it solid made it read as the loudest action
 * on the page, outshouting the actual primary.
 *
 * Pass `tone` when the filter selects a status the list badges also carry.
 * Omit it for scope filters ("Assigned to me") that have no status of their
 * own — those engage in the accent wash.
 */
export function Segment({
  active,
  tone,
  className = '',
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  active: boolean;
  tone?: BadgeTone;
}) {
  const on = tone ? SEGMENT_TONE[tone] : 'border-accent bg-secondary text-accent';
  return (
    <button
      type="button"
      aria-pressed={active}
      className={`${BASE} ${SIZE.sm} border ${
        active
          ? on
          : 'border-border bg-sheet text-muted-foreground hover:border-accent/50 hover:text-foreground'
      } ${className}`}
      {...rest}
    />
  );
}
